import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";

import { useAuth } from "../context/AuthContext";
import {
  savePreauthSession,
  clearPreauthSession,
} from "../components/helpers/authFlowStorage";

const shellStyle = {
  maxWidth: "520px",
  margin: "0 auto",
  paddingTop: "2.5rem",
};

const heroStyle = {
  textAlign: "center",
  marginBottom: "1.5rem",
};

const brandTitleStyle = {
  margin: 0,
  fontSize: "2.6rem",
  fontWeight: 800,
  lineHeight: 1.05,
  letterSpacing: "0.3px",
};

const subtitleStyle = {
  margin: "0.75rem 0 0",
  fontSize: "1.05rem",
  fontWeight: 600,
  color: "var(--color-text)",
};

const supportingTextStyle = {
  margin: "0.4rem 0 0",
  fontSize: "0.95rem",
  color: "var(--color-text-muted)",
};

const languageSwitcherStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  marginTop: "12px",
};

const footerStyle = {
  marginTop: "1rem",
  textAlign: "center",
};

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearStoredSession } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");

  useEffect(() => {
    clearStoredSession();
    clearPreauthSession();

    const prefill = location.state?.phoneNumber;
    if (prefill) {
      setPhoneNumber(String(prefill).replace(/\D/g, ""));
    }
  }, [location.state, clearStoredSession]);

  const fetchUser = async (phone) => {
    try {
      // console.log("FULL URL:", `${process.env.REACT_APP_API_URL}/auth/login-phone`);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user-by-phone`,{params: { phone_number: phone },});

      setName(res.data?.name || "");
      setSurname(res.data?.surname || "");
    } catch {
      setName("");
      setSurname("");
    }
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setPhoneNumber(digits);
  };

  const handlePasswordChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setPassword(digits);
  };

  const handlePhoneBlur = () => {
    if (phoneNumber.length >= 7) {
      fetchUser(phoneNumber);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
  };

  const getLangButtonStyle = (lng) => ({
    border: "1px solid var(--color-border)",
    background:
      i18n.language?.toLowerCase().startsWith(lng)
        ? "var(--color-accent)"
        : "var(--color-surface, white)",
    color:
      i18n.language?.toLowerCase().startsWith(lng)
        ? "white"
        : "var(--color-text)",
    borderRadius: "8px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.9rem",
    minWidth: "52px",
    transition: "all 0.2s ease",
  });

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    const form = new FormData(e.target);
    const phone = String(form.get("phone") || "").replace(/\D/g, "");
    const pin = String(form.get("password") || "").replace(/\D/g, "");

    if (!phone || !pin) {
      alert(t("login.errors.missing_phone_or_pin"));
      return;
    }

    if (!/^\d{4}$/.test(pin)) {
      alert(t("login.errors.invalid_pin"));
      return;
    }

    try {
      setIsLoading(true);

      clearStoredSession();
      clearPreauthSession();

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login-phone`,
        {
          phone_number: phone,
          password: pin,
        }
      );

      const resolvedPhone = res.data?.phone_number || phone;
      const resolvedPreauthToken = res.data?.preauth_token || "";

      savePreauthSession({
        phoneNumber: resolvedPhone,
        name: res.data?.name || name || "",
        surname: res.data?.surname || surname || "",
        preauthToken: resolvedPreauthToken,
      });

      navigate("/excursion-gate", { replace: true });
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        t("login.errors.login_failed");

      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={shellStyle}>
          <div style={heroStyle}>
            <h1 style={brandTitleStyle}>
              <span style={{ color: "var(--color-text)" }}>IGot</span>
              <span style={{ color: "var(--color-accent)" }}>ThisBill</span>
            </h1>

            <p style={subtitleStyle}>{t("login.subtitle")}</p>
            <p style={supportingTextStyle}>{t("login.supporting_text")}</p>

            <div style={languageSwitcherStyle}>
              <button
                type="button"
                onClick={() => changeLanguage("en")}
                style={getLangButtonStyle("en")}
              >
                EN
              </button>

              <button
                type="button"
                onClick={() => changeLanguage("fr")}
                style={getLangButtonStyle("fr")}
              >
                FR
              </button>

              <button
                type="button"
                onClick={() => changeLanguage("tr")}
                style={getLangButtonStyle("tr")}
              >
                TR
              </button>
            </div>
          </div>

          <Card title={t("login.card_title")}>
            <form onSubmit={handleLogin}>
              <Stack gap="4">
                <FormField label={t("login.phone_label")}>
                  <TextInput
                    name="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder={t("login.phone_placeholder")}
                    autoComplete="tel"
                    disabled={isLoading}
                  />
                </FormField>

                <FormField label={t("login.pin_label")}>
                  <TextInput
                    name="password"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t("login.pin_placeholder")}
                    maxLength={4}
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                </FormField>

                <Button type="submit" fullWidth size="lg" disabled={isLoading}>
                  {isLoading
                    ? t("login.loading_button")
                    : t("login.submit_button")}
                </Button>
              </Stack>
            </form>
          </Card>

          <div style={footerStyle}>
            <p className="form-link-text">
              {t("login.register_prompt")}{" "}
              <Link to="/register" className="form-link">
                {t("login.register_link")}
              </Link>
            </p>
          </div>

          <div style={footerStyle}>
            <p className="form-link-text">
              {t("login.go_home_prompt")}{" "}
              <Link to="/" className="form-link">
                {t("login.go_home_link")}
              </Link>
            </p>
          </div>


        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default Login;